import type { GemSalesConfig, ShopperContext } from "../types";
import { mountAiQuestionChip } from "./canvas";
import type { CanvasStoreContext, SurfaceEmit } from "./canvas";
import { createRemoteSurfaceGenerator, fetchProvenQuestions } from "./client";
import { planPlacement } from "./placement";

/**
 * The final wire: AI-question chips on the storefront, each opening the canvas
 * against the real /surface/generate endpoint. Opt-in (only runs when the SDK
 * is given a surfaceEndpoint), so the shipping pilot is untouched by default.
 *
 * Chip questions/placement here are a P0 heuristic; intent×section×source
 * auto-placement is P3.
 */

/** What the storefront declares it is written in. Empty (or absent) resolves to English. */
function storefrontLocale(): string | null {
  if (typeof document === "undefined") return null;
  return document.documentElement.getAttribute("lang");
}

export interface SurfaceChipsOptions {
  endpoint: string;
  maxChips?: number;
  /** Telemetry sink; wired to the SDK tracker at the runtime. */
  emit?: SurfaceEmit;
  /** Impression governance: mount at most once per session (default true). */
  oncePerSession?: boolean;
}

export interface SurfaceChipsHandle {
  destroy: () => void;
}

const SESSION_KEY = "gemsales.surface.chips.mounted";
/** Pre-rename key — still read so impression governance isn't reset mid-session by the rename. */
const LEGACY_SESSION_KEY = "gemchat.surface.chips.mounted";

export function mountSurfaceChips(
  config: GemSalesConfig,
  context: ShopperContext,
  options: SurfaceChipsOptions,
): SurfaceChipsHandle {
  if (options.oncePerSession !== false && alreadyMountedThisSession()) return { destroy: () => {} };

  const anchor = anchorForChips(findChipAnchor());
  if (!anchor) return { destroy: () => {} };

  // Invite the questions that have actually sold here. Read from this session's cache so the mount
  // stays SYNCHRONOUS — making every shopper wait on a network round-trip (including on stores with
  // no wins, who'd get nothing back for the delay) would be a worse trade than showing the heuristic
  // chips now. The refresh below lands for the next page view, so a session self-corrects after one hop.
  if (disabledThisSession(config.shopId)) return { destroy: () => {} };
  const handle = mountChipsWith(config, context, options, anchor, readCachedProven(config.shopId));

  void fetchProvenQuestions(options.endpoint, config.shopId, { limit: options.maxChips ?? 4 })
    .then((proven) => {
      // null = the merchant has GemSales switched off. Tear the chips down and remember it, so the
      // next page view in this session does not flash them up again before the answer comes back.
      if (proven === null) {
        markDisabled(config.shopId);
        handle.destroy();
        return;
      }
      cacheProven(config.shopId, proven);
    })
    .catch(() => {
      /* proven chips are an upgrade, never a dependency */
    });

  return handle;
}

const PROVEN_KEY = "gemsales.surface.chips.proven";
const LEGACY_PROVEN_KEY = "gemchat.surface.chips.proven";

/** Proven questions learned earlier in this session. Empty on the first view or without storage. */
function readCachedProven(shopId: string): string[] {
  try {
    const raw = sessionStorage.getItem(`${PROVEN_KEY}:${shopId}`) ?? sessionStorage.getItem(`${LEGACY_PROVEN_KEY}:${shopId}`);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? parsed.filter((q): q is string => typeof q === "string") : [];
  } catch {
    return [];
  }
}

const DISABLED_KEY = "gemsales.surface.disabled";

function disabledThisSession(shopId: string): boolean {
  try {
    return sessionStorage.getItem(`${DISABLED_KEY}:${shopId}`) === "1";
  } catch {
    return false;
  }
}

function markDisabled(shopId: string): void {
  try {
    sessionStorage.setItem(`${DISABLED_KEY}:${shopId}`, "1");
  } catch {
    /* no storage — the chips just re-check next view */
  }
}

function cacheProven(shopId: string, questions: string[]): void {
  try {
    sessionStorage.setItem(`${PROVEN_KEY}:${shopId}`, JSON.stringify(questions.slice(0, 6)));
  } catch {
    // sessionStorage unavailable (privacy mode / sandbox) — chips just stay heuristic.
  }
}

function mountChipsWith(
  config: GemSalesConfig,
  context: ShopperContext,
  options: SurfaceChipsOptions,
  anchor: HTMLElement,
  provenQuestions: string[],
): SurfaceChipsHandle {
  const intent = intentOf(context);
  const plan = planPlacement(
    {
      pageType: context.page.type,
      seenSections: context.seenSections,
      locale: storefrontLocale(),
      ...(provenQuestions.length ? { provenQuestions } : {}),
      ...(intent ? { topIntent: intent.cluster, intentScore: intent.score } : {}),
      ...(context.acquisition
        ? { acquisition: { medium: context.acquisition.medium, campaign: context.acquisition.campaign, source: context.acquisition.source } }
        : {}),
    },
    { maxQuestions: options.maxChips ?? 4 },
  );

  const generate = createRemoteSurfaceGenerator(options.endpoint);
  const storeContext = buildStoreContext(config, context, plan.depth, plan.cold, plan.angle);

  const mounted = plan.questions.map((question) =>
    mountAiQuestionChip({
      question,
      anchor,
      placement: "afterend",
      canvas: {
        generate,
        context: storeContext,
        ...(plan.anchorSection ? { sectionAnchor: plan.anchorSection } : {}),
        ...(options.emit ? { emit: options.emit } : {}),
      },
    }),
  );

  if (options.oncePerSession !== false) markMountedThisSession();
  options.emit?.("gemsales.surface.chips_mounted", { count: mounted.length, proven: provenQuestions.length, angle: plan.angle, depth: plan.depth });

  return { destroy: () => mounted.forEach((chip) => chip.destroy()) };
}

function alreadyMountedThisSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1" || sessionStorage.getItem(LEGACY_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function markMountedThisSession(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, "1");
  } catch {
    // sessionStorage unavailable (privacy mode / sandbox) — governance degrades gracefully.
  }
}

function buildStoreContext(
  config: GemSalesConfig,
  context: ShopperContext,
  depth: CanvasStoreContext["depth"],
  cold: boolean,
  angle: string | null,
): CanvasStoreContext {
  const product = pickProduct(config, context);
  const intent = intentOf(context);
  // Traffic source → generation. The prompt already has a "Traffic angle" line, but nothing populated
  // ctx.source, so it was dead. Forward the placement-chosen angle + the shopper's real acquisition
  // (campaign, referrer) so the copy can lead with why THIS visitor arrived.
  const acq = context.acquisition;
  const source = {
    ...(angle ? { angle } : {}),
    ...(acq?.campaign ? { campaign: acq.campaign } : {}),
    ...(acq?.referrer ? { referrer: acq.referrer } : {}),
  };
  return {
    store: config.shopId,
    ...(product ? { product } : {}),
    ...(intent ? { intentCluster: intent.cluster, intentScore: intent.score } : {}),
    ...(depth ? { depth } : {}),
    ...(cold ? { cold: true } : {}),
    ...(Object.keys(source).length ? { source } : {}),
    // Shopper identity + cart → the surface can personalize and complete what's already in the basket.
    ...(context.anonymousId ? { anonymousId: context.anonymousId } : {}),
    ...(typeof context.page?.cartSubtotal === "number" ? { cartSubtotal: context.page.cartSubtotal } : {}),
    ...(context.cartItems?.length ? { cartItems: context.cartItems } : {}),
  };
}

interface PickedProduct {
  ref: string;
  title: string;
  price?: number;
  image?: string;
}

function pickProduct(config: GemSalesConfig, context: ShopperContext): PickedProduct | null {
  const handle = context.page.productHandle;
  const entries = Object.entries(config.products);
  if (handle) {
    const match = entries.find(([, product]) => product.handle === handle);
    if (match) return fromConfig(match[0], match[1]);
  }
  // No configured catalog match — read the current PDP's product from schema.org
  // Product JSON-LD. Headless storefronts (and most Shopify themes) emit it, so
  // the surface works on ANY store with structured data — a site-wide script tag
  // is enough, no per-store products config or per-PDP wiring required. The product
  // photo comes from the same node so the surface shows a real image, not a fabricated one.
  const fromLd = productFromJsonLd(handle);
  if (fromLd) return fromLd;
  const first = entries[0];
  return first ? fromConfig(first[0], first[1]) : null;
}

function fromConfig(ref: string, product: GemSalesConfig["products"][string]): PickedProduct {
  const image = imageFromValue((product as { image?: unknown }).image);
  return { ref, title: product.title, ...(product.price != null ? { price: product.price } : {}), ...(image ? { image } : {}) };
}

/** Read {ref,title,price,image} from the page's schema.org Product JSON-LD, if present. */
function productFromJsonLd(handle?: string | null): PickedProduct | null {
  if (typeof document === "undefined") return null;
  const scripts = Array.from(document.querySelectorAll('script[type="application/ld+json"]'));
  for (const script of scripts) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(script.textContent ?? "");
    } catch {
      continue;
    }
    const node = findProductNode(parsed);
    if (!node) continue;
    const title = typeof node.name === "string" ? node.name : "";
    if (!title) continue;
    const price = priceFromOffers(node.offers);
    const image = imageFromValue(node.image);
    const ref = handle || skuOf(node) || slugify(title);
    return { ref, title, ...(price != null ? { price } : {}), ...(image ? { image } : {}) };
  }
  return null;
}

/** schema.org `image` may be a URL string, an array, or an ImageObject {url}. */
function imageFromValue(value: unknown): string | undefined {
  if (typeof value === "string") return httpUrl(value);
  if (Array.isArray(value)) {
    for (const item of value) {
      const url = imageFromValue(item);
      if (url) return url;
    }
    return undefined;
  }
  if (value && typeof value === "object") {
    const url = (value as Record<string, unknown>).url ?? (value as Record<string, unknown>).contentUrl;
    return typeof url === "string" ? httpUrl(url) : undefined;
  }
  return undefined;
}

function httpUrl(value: string): string | undefined {
  const v = value.trim();
  return /^https?:\/\//i.test(v) ? v : undefined;
}

/** Find a `@type: Product` node in a JSON-LD value (object, array, or @graph). */
function findProductNode(value: unknown): Record<string, unknown> | null {
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findProductNode(item);
      if (found) return found;
    }
    return null;
  }
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  if (isProductType(record["@type"])) return record;
  if (Array.isArray(record["@graph"])) return findProductNode(record["@graph"]);
  return null;
}

function isProductType(type: unknown): boolean {
  if (typeof type === "string") return type === "Product" || type.endsWith("/Product");
  if (Array.isArray(type)) return type.some(isProductType);
  return false;
}

function priceFromOffers(offers: unknown): number | undefined {
  const first = Array.isArray(offers) ? offers[0] : offers;
  if (!first || typeof first !== "object") return undefined;
  const raw = (first as Record<string, unknown>).price ?? (first as Record<string, unknown>).lowPrice;
  const price = typeof raw === "string" ? Number.parseFloat(raw) : typeof raw === "number" ? raw : NaN;
  return Number.isFinite(price) ? price : undefined;
}

function skuOf(node: Record<string, unknown>): string | undefined {
  const sku = node.sku ?? node.productID ?? node.mpn;
  return typeof sku === "string" && sku ? sku : undefined;
}

function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "product";
}

function intentOf(context: ShopperContext): { cluster: string; score: number } | null {
  const top = context.intentProfile.topIntent;
  if (!top) return null;
  const score = context.intentProfile.scores[top];
  return { cluster: top, score: typeof score === "number" ? score : 0.6 };
}

/**
 * Never split a heading from the copy it introduces.
 *
 * The chips mount `afterend` of the anchor, and the fallback chain reaches `main h1` / `h1` on any page
 * without a buy form — a landing page, an advertorial, an editorial. The result lands the chips BETWEEN
 * the headline and the paragraph that belongs to it: measured on three separate renders of a real
 * storefront landing page, where two prompt pills sit on top of the hero copy and read as a broken page.
 *
 * A heading and its copy are one unit, so the chips go after that unit. Walking to the heading's parent
 * keeps them in the hero (where an invitation to ask belongs) without cutting a sentence in half. Any
 * other anchor — a buy form, a section, `main` — already sits at a seam and is returned unchanged.
 */
function anchorForChips(anchor: HTMLElement | null): HTMLElement | null {
  if (!anchor) return anchor;
  if (!/^H[1-6]$/.test(anchor.tagName)) return anchor;

  // Two steps, and conflating them is what made my first attempt ship without moving anything.
  //
  // 1. Walk out of any wrapper that carries NOTHING BUT the heading. Element-built storefronts wrap
  //    every node in its own container, so the heading's parent is often just the heading's box again.
  //    The test is "adds no text of its own", not a tag list: the wrapper may be a div, a span with
  //    `display:contents`, or a section holding only the heading.
  let box: HTMLElement = anchor;
  const heading = (anchor.textContent ?? "").trim();
  while (true) {
    const parent = box.parentElement;
    if (!parent || parent === document.body || parent.tagName === "MAIN") break;
    if ((parent.textContent ?? "").trim() !== heading) break;
    box = parent;
  }

  // 2. Step to the block that holds the heading AND the copy it introduces, and sit after THAT. A
  //    headline and its paragraph are one unit; landing between them reads as a broken page (measured
  //    on three renders of a live storefront landing page).
  const parent = box.parentElement;
  // `main`/`body` are not a unit to step past — that would drop the chips at the end of the whole page,
  // far from the copy that earned the question.
  if (!parent || parent === document.body || parent.tagName === "MAIN") return box;
  let unit: HTMLElement = parent;

  // 3. Keep climbing while the insertion point's PARENT lays its children out as grid or flex.
  //
  //    `insertAdjacentElement("afterend")` makes the chip a SIBLING — and a sibling of a grid item is
  //    another GRID ITEM. Shipping step 2 alone fixed the hero copy and then scattered the two chips
  //    into whatever cells the hero's two-column grid had spare: one floating beside the photo, one
  //    stranded below. Measured on the live page, which is the only reason I know; the DOM looked
  //    correct either way.
  //
  //    Normal block flow is the only context where "after this block" means what it looks like.
  while (isLaidOutByParent(unit)) {
    const next: HTMLElement | null = unit.parentElement;
    if (!next || next === document.body || next.tagName === "MAIN") return unit;
    unit = next;
  }
  return unit;
}

/**
 * True when something above this element positions its children, so a new sibling becomes a cell.
 *
 * It must see THROUGH `display: contents`. That is exactly what a gem element wrapper uses: the wrapper
 * box does not lay anything out, its children join the GRANDPARENT's grid. Checking only the immediate
 * parent therefore answered "block, you're fine" while the chip was landing in the hero's two-column
 * grid — measured on the live page after shipping the parent-only check.
 */
function isLaidOutByParent(element: HTMLElement): boolean {
  let parent = element.parentElement;
  while (parent) {
    const display = window.getComputedStyle(parent).display;
    // A `contents` box is not a layout context; whatever is above it lays these children out.
    if (display !== "contents") {
      return display === "grid" || display === "inline-grid" || display === "flex" || display === "inline-flex";
    }
    parent = parent.parentElement;
  }
  return false;
}

function findChipAnchor(): HTMLElement | null {
  const selectors = [
    "[data-gemsales-slot]",
    // Pre-rename slot attribute: theme markup pasted from an earlier snippet may still carry it.
    "[data-gemchat-slot]",
    ".product-form",
    "form[action*='/cart']",
    "[data-product-form]",
    "main h1",
    "h1",
    "main",
  ];
  // Take the first VISIBLE match, not the first match. Themes routinely ship a hidden duplicate of
  // the buy form inside a cart drawer or a mobile menu; anchoring to one of those injects the chips
  // into a `visibility:hidden` container and pushes them off-screen, where they mount, emit an
  // impression, and are seen by nobody. Every candidate is now checked before it is trusted.
  for (const selector of selectors) {
    for (const element of Array.from(document.querySelectorAll<HTMLElement>(selector))) {
      if (isRenderable(element)) return element;
    }
  }
  return document.body ?? null;
}

/** Laid out, painted, and not inside a hidden ancestor. */
function isRenderable(element: HTMLElement): boolean {
  if (!element.isConnected) return false;
  // offsetParent is null for display:none subtrees (and for position:fixed, hence the rect check).
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  const style = window.getComputedStyle(element);
  if (style.visibility === "hidden" || style.display === "none" || style.opacity === "0") return false;
  // `visibility` inherits, so a hidden drawer marks its children hidden too — but `display:none`
  // ancestors zero the rect above, so both cases are covered.
  return true;
}
